import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaixarContaRequest, ContasAPagar, ContasAPagarPaginado, CriarContaAPagarParcelada, ParcelamentoContasAPagar, ResumoContasAVencer, Orcamento, OrcamentoLista, OrcamentoDTO, OrcamentoPaginado, OrcamentoItemDTO, FecharVendaDTO, VendaDTO, VendaPaginado, ContasAReceberResumoPaginado, VendaDetalheDTO, ContaAReceberVendaItemDTO, BaixarParcelaCartaoDTO, OrcamentoEspelhoDTO, InadimplentesPaginado, ContasAReceberClientePaginado, ResumoMovimentoFinanceiro, VendaCancelamentoSumario, LogVendaRequest, LogVendaResponse } from '../models/financeiro.model';
import { ConfigService } from '../core/config/config.service';


@Injectable({
  providedIn: 'root'
})
export class FinanceiroService {

  private readonly configService = inject(ConfigService);
  private get loginUrl(): string {
    const baseUrl = this.configService.settings()?.apiUrl;
    return `${baseUrl}/financeiro`;
  }


  constructor(private http: HttpClient) { }

  getResumoMovimentoFinanceiro(dataInicio: string, dataFim: string): Observable<ResumoMovimentoFinanceiro> {
    const params = new HttpParams()
      .set('dataInicio', dataInicio)
      .set('dataFim', dataFim);
    return this.http.get<any>(
      `${this.loginUrl}/movimento-financeiro/resumo`, { params }
    ).pipe(
      map(r => ({
        totalEntrada: r.totalEntrada ?? r.totalEntradas ?? r.entrada ?? r.totalRecebido ?? 0,
        totalSaida:   r.totalSaida   ?? r.totalSaidas   ?? r.saida   ?? r.totalPago     ?? 0,
        totalFaturado: r.totalFaturado ?? r.faturado ?? 0,
        saldo:        r.saldo        ?? r.saldoFinal     ?? 0
      }))
    );
  }

  getOrcamentosAbertos(page: number = 0, size: number = 10): Observable<OrcamentoPaginado> {
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));
    return this.http.get<OrcamentoPaginado>(this.loginUrl + '/orcamentos/abertos', { params });
  }

  buscarOrcamentosAbertos(termoBusca: string): Observable<OrcamentoDTO[]> {
    const params = new HttpParams().set('termoBusca', termoBusca);
    return this.http.get<OrcamentoDTO[]>(this.loginUrl + '/orcamentos/abertos/busca', { params });
  }

  getOrcamentoById(id: number): Observable<Orcamento> {
    return this.http.get<Orcamento>(this.loginUrl + '/orcamentos/' + id);
  }

  getEspelhoOrcamento(id: number): Observable<OrcamentoEspelhoDTO> {
    return this.http.get<OrcamentoEspelhoDTO>(this.loginUrl + '/orcamentos/' + id + '/espelho');
  }


   getItensOrcamentoByIdOrcamento(id: number): Observable<OrcamentoItemDTO[]> {
    return this.http.get<OrcamentoItemDTO[]>(this.loginUrl + '/orcamento-itens/orcamento/' + id);
  }

  createOrcamento(orcamento: Orcamento): Observable<Orcamento> {
    return this.http.post<Orcamento>(this.loginUrl + '/orcamentos', orcamento);
  }

  updateOrcamento(id: number, orcamento: Orcamento): Observable<Orcamento> {
    return this.http.put<Orcamento>(this.loginUrl + '/orcamentos/' + id, orcamento);
  }

   updateItensOrcamento(itens: OrcamentoItemDTO[], orcamentoid: number): Observable<OrcamentoItemDTO[]> {
    return this.http.put<OrcamentoItemDTO[]>(this.loginUrl + '/orcamento-itens/orcamento/' + orcamentoid, itens);
  }


  getContasAPagar(page: number, size: number, searchTerm: string): Observable<ContasAPagarPaginado> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }
    
    return this.http.get<ContasAPagarPaginado>(this.loginUrl + '/contas-a-pagar/paginated', { params });
  }

  getContasAPagarPorFornecedor(page: number, size: number, searchTerm: string): Observable<ContasAPagarPaginado> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (searchTerm) {
      params = params.set('razaoSocial', searchTerm);
    }
    
    return this.http.get<ContasAPagarPaginado>(this.loginUrl + '/contas-a-pagar/fornecedor', {params});
  }


  getContasAPagarGenerico(page: number, size: number, searchTerm: string): Observable<ContasAPagarPaginado> {
      let params = new HttpParams()
        .set('page', page.toString())
        .set('size', size.toString());

      if (searchTerm) {
        params = params.set('generico', searchTerm);
      }
    
    return this.http.get<ContasAPagarPaginado>(this.loginUrl + '/contas-a-pagar/generico', {params});
  }




  getResumoContasAVencer(dias: number): Observable<ResumoContasAVencer> {
    const params = new HttpParams().set('dias', dias.toString());
    return this.http.get<ResumoContasAVencer>(this.loginUrl + '/contas-a-pagar/a-vencer', { params });
  }

  getContasAPagarById(id: number): Observable<ContasAPagar> {
    return this.http.get<ContasAPagar>(this.loginUrl + '/contas-a-pagar/' + id);
  }

  createContasAPagar(conta: ContasAPagar): Observable<ContasAPagar> {
    return this.http.post<ContasAPagar>(this.loginUrl + '/contas-a-pagar', conta);
  }

  updateContasAPagar(conta: ContasAPagar): Observable<ContasAPagar> {
    return this.http.put<ContasAPagar>(this.loginUrl + '/contas-a-pagar/' + conta.id, conta);
  }

  createContasAPagarParcelada(payload: CriarContaAPagarParcelada): Observable<ParcelamentoContasAPagar> {
    return this.http.post<ParcelamentoContasAPagar>(this.loginUrl + '/contas-a-pagar/parcelada', payload);
  }

  simularParcelamento(payload: CriarContaAPagarParcelada): Observable<ParcelamentoContasAPagar> {
    return this.http.post<ParcelamentoContasAPagar>(this.loginUrl + '/contas-a-pagar/parcelada/simulacao', payload);
  }

  baixarConta(payload: BaixarContaRequest): Observable<void> {
    return this.http.put<void>(this.loginUrl + '/contas-a-pagar/baixar', payload);
  }

  deleteContasAPagar(id: number): Observable<void> {
    return this.http.delete<void>(this.loginUrl + '/contas-a-pagar/' + id);
  }


  efetuarVenda(payload: FecharVendaDTO): Observable<number> {
      return this.http.post<number>(this.loginUrl + '/vendas', payload);
  }

  baixarParcelaVendaMista(payload: FecharVendaDTO): Observable<number> {
      return this.http.post<number>(this.loginUrl + '/vendas/baixarParcelaVendaMista', payload);
  }


  baixarParcelaCartao(payload: BaixarParcelaCartaoDTO): Observable<number> {
      return this.http.post<number>(this.loginUrl + '/vendas/baixarParcelaCartao', payload);
  }


  buscarVendas(id?: number, nomeCliente?: string): Observable<VendaDTO[]> {
    let params = new HttpParams();
    if (id) params = params.set('id', id.toString());
    if (nomeCliente) params = params.set('nomeCliente', nomeCliente);
    return this.http.get<VendaDTO[]>(this.loginUrl + '/vendas/busca', { params });
  }

  getVendas(page: number, size: number): Observable<VendaPaginado> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<VendaPaginado>(this.loginUrl + '/vendas/paginated', { params });
  }

  getVendaById(idVenda: number): Observable<VendaDetalheDTO> {
    return this.http.get<VendaDetalheDTO>(this.loginUrl + '/vendas/' + idVenda);
  }

  getSumarioCancelamentoVenda(idVenda: number): Observable<VendaCancelamentoSumario> {
    return this.http.get<VendaCancelamentoSumario>(this.loginUrl + '/vendas/' + idVenda + '/cancelamento/sumario');
  }

  cancelarVenda(idVenda: number): Observable<void> {
    return this.http.put<void>(this.loginUrl + '/vendas/' + idVenda + '/cancelar', {});
  }

  getInadimplentes(page: number, size: number): Observable<InadimplentesPaginado> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    return this.http.get<InadimplentesPaginado>(this.loginUrl + '/contas-a-receber/inadimplentes/paginated', { params });
  }

  getContasAReceberResumo(page: number, size: number, search?: string): Observable<ContasAReceberResumoPaginado> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    const termo = (search ?? '').trim();
    if (termo) {
      params = params.set('search', termo);
    }

    return this.http.get<ContasAReceberResumoPaginado>(this.loginUrl + '/contas-a-receber/resumo/paginated', { params });
  }

  getContasAReceberByVenda(idVenda: number): Observable<ContaAReceberVendaItemDTO[]> {
    return this.http.get<ContaAReceberVendaItemDTO[]>(this.loginUrl + '/contas-a-receber/venda/' + idVenda + '/detalhes');
  }

  getContasAReceberPorCliente(nomeCliente: string, page: number, size: number): Observable<ContasAReceberClientePaginado> {
    const params = new HttpParams()
      .set('nomeCliente', nomeCliente)
      .set('page', page.toString())
      .set('size', size.toString());

    return this.http.get<ContasAReceberClientePaginado>(this.loginUrl + '/contas-a-receber/cliente', { params });
  }

  criarLogVenda(payload: LogVendaRequest): Observable<LogVendaResponse> {
    return this.http.post<LogVendaResponse>(this.loginUrl + '/log-vendas', payload);
  }

  getLogsVendaByIdVenda(idVenda: number): Observable<LogVendaResponse[]> {
    return this.http.get<LogVendaResponse[]>(this.loginUrl + '/log-vendas/venda/' + idVenda);
  }

}

